/* ──────────────────────────────────────────────────────────────────────────────
   Faz 5 — Depozito İade İrsaliyesi + Mutabakat · ORTAK KATMAN

   ══ TUTAR YOKTUR ══════════════════════════════════════════════════════════════
   Depozito TİCARİ BİR İŞLEM DEĞİLDİR — emanetin (sepet/kasa/cam şişe) geri
   verilmesidir. Belgede tutar HESAPLANMAZ ve hiçbir kolonda/alanda para
   GÖSTERİLMEZ; "₺0,00" bile yazılmaz (spec §6, iş kuralı 4). Bu dosyada bilinçli
   olarak `fmtMoney` YOKTUR ve returnShared'dan da ALINMAZ — bir ekran yanlışlıkla
   para kolonu ekleyemesin diye.

   Karşılığı para değil, DEPOZİTO BAKİYESİNİN azalmasıdır.

   ══ SAYIM ≠ İADE ══════════════════════════════════════════════════════════════
   `DepositCountsPage` (/deposit-counts) SAYIM belgesidir ("araçta kaç sepet var")
   ve ledger'a dokunmaz. Buradaki belge İADE irsaliyesidir ("şu sepetleri Sütaş'a
   geri verdim") ve depozito stoğunu DÜŞÜRÜR. İki ayrı hat; biri diğerinin yerine
   geçmez.
   ────────────────────────────────────────────────────────────────────────────── */
import { odataRows } from './returnShared';
import { escapeODataString } from './c360/filterModel';

export const RETURNS_API = '/api/deposit-returns';
export const BALANCES_API = '/api/deposit-balances';

/** Backend: Static/DepositReturnStatuses.cs — 0=DRAFT, 1=SENT, 2=CANCELLED. */
export const DEPOSIT_STATUS = { DRAFT: 0, SENT: 1, CANCELLED: 2 };

const STATUS_META = {
  0: { label: 'Taslak', tone: 'neutral' },
  1: { label: 'Gönderildi', tone: 'ok' },
  2: { label: 'İptal', tone: 'bad' },
};

export const statusLabel = (v) => STATUS_META[Number(v)]?.label || '-';
export const statusTone = (v) => STATUS_META[Number(v)]?.tone || 'neutral';

export const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Durum: Tümü' },
  { value: '0', label: 'Taslak' },
  { value: '1', label: 'Gönderildi' },
  { value: '2', label: 'İptal' },
];

/**
 * c360 kolon filtresi (huni) için Durum seçenekleri. "Tümü" satırı YOKTUR: c360'ta filtreyi
 * kaldırmak huninin kendi "Temizle" düğmesinin işidir (boş değer → clearColumnFilter).
 * Değerler SAYIDIR → OData'ya `Status eq 1` olarak çıkar (filterModel.buildFilterClause).
 */
export const STATUS_SELECT_OPTIONS = [
  { value: 0, label: 'Taslak' },
  { value: 1, label: 'Gönderildi' },
  { value: 2, label: 'İptal' },
];

/** Stok/bakiye YALNIZ kesilmiş (SENT) belgede düşer — taslak fiziksel bir olay değildir. */
export const isPosted = (v) => Number(v) === DEPOSIT_STATUS.SENT;
/** Kesilmiş belge İPTAL EDİLEMEZ: mal araçtan çıkmıştır (backend de reddeder). */
export const isCancellable = (v) => Number(v) === DEPOSIT_STATUS.DRAFT;

/** Baskıda tutar yerine basılan sabit ibare (DepositReturnsController.DepositPrintStatement). */
export const PRINT_STATEMENT = 'Bedelsiz — depozito iadesi';

/* ── EŞİKLER ────────────────────────────────────────────────────────────────────
   Spec §12.4 "depozito bakiyesi eşiği ne?" sorusunu AÇIK BIRAKIR. Ekranın çalışması
   için bir başlangıç değeri şart; ikisi de kullanıcı tarafından ekrandan değiştirilebilir
   (mutabakat ekranındaki eşik kutuları) — sabit bir iş kuralı DEĞİL, bir izleme çizgisidir. */

/** Kırık oranı uyarı eşiği (%). Sürekli aşan plasiyer = taşıma sorunu ya da SUİSTİMAL (spec §8). */
export const DAMAGE_RATE_THRESHOLD = 10;
/** "Yüksek depozito borcu" eşiği (adet). Sepet PARA demektir; sahada birikmesi istenmez. */
export const HIGH_DEBT_THRESHOLD = 300;
/** Kaç gündür iade yok — bu günden fazlası kırmızı. */
export const NO_RETURN_DAYS_THRESHOLD = 30;
/** Bir plasiyerin "sürekli" yüksek kırık bildirdiğini söyleyebilmek için gereken en az belge sayısı. */
export const DAMAGE_MIN_DOCS = 2;

/** Kırık oranı (%). Adet 0 ise oran TANIMSIZDIR (0 değil) — null döner, ekran "—" basar. */
export function damageRate(quantity, damaged) {
  const q = Number(quantity || 0);
  const d = Number(damaged || 0);
  if (q <= 0) return null;
  return (d / q) * 100;
}

export function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `%${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
}

/** Verilen andan bugüne geçen tam gün. Değer yoksa null ("hiç iade yok"). */
export function daysSince(v) {
  if (!v) return null;
  const t = new Date(v).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
}

/* ── ÜRÜN SINIFI ────────────────────────────────────────────────────────────────
   Spec §8 blok 2 "Sepet · Şişe" kolonları ister. Backend'de depozito ürününün ALT TİPİ
   YOKTUR (Product.DefaultStockType yalnız "Depozito" der) → sınıflandırma ürün ADINDAN
   yapılır. Eşleşmeyen ürün YUTULMAZ, "Diğer" kolonunda görünür: kimse bakiyesini
   kaybetmesin diye. */
const CRATE_RE = /(sepet|kasa|krate|crate)/i;
const BOTTLE_RE = /(şişe|sise|bottle|cam)/i;

export function depositClass(productName) {
  const n = String(productName || '');
  if (CRATE_RE.test(n)) return 'CRATE';
  if (BOTTLE_RE.test(n)) return 'BOTTLE';
  return 'OTHER';
}

/* ── OData FİLTRE CÜMLELERİ (c360 `baseFilter`) ─────────────────────────────────
   Liste artık c360 (useC360List) ile SUNUCU TARAFINDA sayfalanır/sıralanır. Ekranın tepesindeki
   kapsam filtreleri (tarih · yüksek kırık) bu yüzden client-side `Array.filter` değil, OData
   $filter cümlesi olmak zorunda: 2. sayfadaki bir belge 1. sayfada bellekte YOKTUR. */

/**
 * Belge tarihi TÜRETİLMİŞTİR: kesilmişse SentAt, kesilmemişse oluşturulma anı. Tek bir
 * `DocumentDate` kolonu YOKTUR (bilinçli — taslağın "belge tarihi" olmaz).
 */
export const documentDateOf = (row) => row?.sentAt ?? row?.SentAt ?? row?.createdDate ?? row?.CreatedDate ?? null;

/**
 * "Son N gün" OData cümlesi. `documentDateOf` ile BİREBİR aynı kuralı kurar: yalnız CreatedDate'e
 * filtre kurmak, geç kesilen (SentAt >> CreatedDate) belgeleri ekranın gösterdiği tarihe rağmen
 * eler ve listeyi sessizce yalanlardı.
 */
export function recentDaysFilter(days) {
  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0) return '';
  const sinceIso = new Date(Date.now() - n * 86400000).toISOString();
  return `((SentAt ne null and SentAt ge ${sinceIso}) or (SentAt eq null and CreatedDate ge ${sinceIso}))`;
}

/**
 * "Yüksek kırık oranı" OData cümlesi. Oran SAKLANMAZ (türetilmiştir) → eşik karşılaştırması
 * bölmeyle değil ÇARPMAYLA yazılır:  damaged/qty*100 ≥ T  ⇔  damaged*100 ≥ qty*T.
 * Adet 0 iken oran TANIMSIZDIR (0 değil) → o satırlar kapsam DIŞIDIR; `damageRate` ile aynı kural.
 */
export function highDamageFilter(threshold = DAMAGE_RATE_THRESHOLD) {
  return `(TotalQuantity gt 0 and TotalDamaged mul 100 ge TotalQuantity mul ${Number(threshold)})`;
}

/** Belge kaleminde ürün adı arayan OData cümlesi (mutabakat ekranındaki "Ürün" filtresi). */
export function productLineFilter(query) {
  const q = escapeODataString(String(query || '').trim()).toLowerCase();
  if (!q) return '';
  return `Lines/any(l: contains(tolower(l/ProductName),'${q}'))`;
}

/**
 * `Lines/any(...)` bu uçta destekleniyor mu? DepositReturnHeader'ın OData modeli CLR tipinden
 * KONVANSİYONLA üretilir; koleksiyon navigasyonu modele girmemişse any() 400 döner.
 *
 * Desteklenmiyorsa ürün filtresi UYGULANMAZ (satır ELENMEZ) — bilinmeyeni yokmuş gibi saymak
 * listeyi sessizce boşaltırdı. Bu, $expand başarısız olduğunda eski ekranın yaptığının aynısıdır;
 * fark, artık listeyi kırma riski olmadan önceden ÖĞRENİYOR olmamız. Sonuç süreç boyu cache'lenir.
 */
let lineFilterProbe = null;
export function supportsLineFilter(headers) {
  if (!lineFilterProbe) {
    lineFilterProbe = (async () => {
      try {
        const url = `${RETURNS_API}?$top=1&$filter=${encodeURIComponent('Lines/any(l: l/Quantity gt 0)')}`;
        const res = await fetch(url, { headers });
        return res.ok;
      } catch {
        return false;
      }
    })();
  }
  return lineFilterProbe;
}

/* ── VERİ ÇEKME ─────────────────────────────────────────────────────────────── */

/**
 * Kırık örüntüsü için yalın çekim (3 alan). Liste sayfasından AYRIDIR ve bilerek öyledir:
 *
 * Bant "hangi plasiyer SÜREKLİ yüksek kırık bildiriyor" sorusuna cevap verir; bu soru ancak
 * FİLTRENİN TÜMÜ görülünce cevaplanabilir. Sayfalanmış 10 satıra bakan bir toplulaştırma,
 * 2. sayfadaki belgeleri göremediği için örüntüyü KAÇIRIR — yani bandı yalancı yapar.
 * `filterParams` listenin o anki OData $filter'ıdır (useC360List.buildCurrentFilterParams).
 */
export async function fetchDamageStats(headers, filterParams, { take = 2000 } = {}) {
  let url = `${RETURNS_API}?$top=${take}&$select=${encodeURIComponent('Id,RepresentativeId,TotalQuantity,TotalDamaged')}`;
  if (filterParams) url += `&$filter=${encodeURIComponent(filterParams)}`;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error('Kırık oranı özeti alınamadı');
  return odataRows(await res.json()).map((r) => {
    const quantity = Number(r.totalQuantity ?? r.TotalQuantity ?? 0);
    const damaged = Number(r.totalDamaged ?? r.TotalDamaged ?? 0);
    return {
      id: r.id ?? r.Id,
      representativeId: r.representativeId ?? r.RepresentativeId ?? null,
      totalQuantity: quantity,
      totalDamaged: damaged,
      damageRate: damageRate(quantity, damaged),
    };
  });
}

/**
 * Plasiyer bazında kırık örüntüsü: TEK belgede yüksek kırık KAZA olabilir; TEKRAR EDEN yüksek
 * kırık olmaz (spec §8). Bu yüzden eşik (`DAMAGE_RATE_THRESHOLD`) YANINDA bir asgari belge
 * sayısı (`DAMAGE_MIN_DOCS`) de aranır.
 */
export function suspiciousRepsFrom(stats) {
  const byRep = new Map();
  (stats || []).forEach((r) => {
    if (!r.representativeId) return;
    const acc = byRep.get(r.representativeId) || { docs: 0, quantity: 0, damaged: 0 };
    acc.docs += 1;
    acc.quantity += Number(r.totalQuantity || 0);
    acc.damaged += Number(r.totalDamaged || 0);
    byRep.set(r.representativeId, acc);
  });
  return [...byRep.entries()]
    .map(([id, a]) => ({ id, ...a, rate: damageRate(a.quantity, a.damaged) }))
    .filter((x) => x.docs >= DAMAGE_MIN_DOCS && x.rate !== null && x.rate >= DAMAGE_RATE_THRESHOLD)
    .sort((a, b) => b.rate - a.rate);
}

/**
 * Mutabakat satırları. `mismatchOnly=false` ile TÜM bakiyeler tek atımda inip ekranda
 * ayrıştırılır: blok 1 (mutabakatsız) ile blok 2 (yüksek borç) aynı veriden beslenir,
 * iki ayrı istek atmaya gerek yok.
 *
 * ⚠ ExternalBalance NULL = SAP henüz veri göndermedi. Bu bir FARK değil BOŞLUKTUR;
 * mutabakatsız SAYILMAZ (backend de saymaz) — aksi halde entegrasyon açılmadan önce
 * ekran tüm plasiyerleri kırmızı gösterirdi.
 */
export async function fetchReconciliation(headers) {
  const res = await fetch(`${BALANCES_API}/reconciliation?mismatchOnly=false`, { headers });
  if (!res.ok) throw new Error('Mutabakat verisi alınamadı');
  const body = await res.json();
  const data = body?.data ?? body?.Data ?? body;
  const items = data?.items ?? data?.Items ?? [];
  return items.map((b) => {
    const ours = Number(b.ours ?? 0);
    const ext = b.externalBalance ?? b.ExternalBalance ?? null;
    return {
      ...b,
      ours,
      externalBalance: ext === null || ext === undefined ? null : Number(ext),
      // Fark yalnız SAP verisi VARSA anlamlıdır.
      difference: ext === null || ext === undefined ? null : ours - Number(ext),
      atCustomer: Number(b.atCustomer ?? 0),
      onVehicle: Number(b.onVehicle ?? 0),
      cls: depositClass(b.productName),
    };
  });
}

export const isMismatch = (row) => row.difference !== null && Math.abs(row.difference) > 0.0001;
